import { saveAs } from "file-saver";
import React from "react";
import { Download, Lock, FileText } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext.jsx";
import { t } from "../utils/translations";

const isFiniteNumber = (v) =>
  typeof v === "number" && Number.isFinite(v);

/**
 * Akzeptiert ausschließlich reine numerische Texte.
 *
 * Beispiele:
 *   "1372.540013606318223529"
 *   "0,01"
 *   "-0.0000001"
 *   "1.2e-7"
 *
 * IDs, Hashes, Datumsangaben und Event-Keys werden nicht verändert.
 */
const NUMERIC_TEXT_RE =
  /^[+-]?(?:(?:\d+(?:[.,]\d*)?)|(?:[.,]\d+))(?:[eE][+-]?\d+)?$/;

/**
 * Wandelt einen numerischen String ohne Number()-Konvertierung in
 * einen normalen Dezimalstring um.
 *
 * Dadurch bleiben auch Tokenmengen mit mehr als 18 Nachkommastellen
 * vollständig erhalten.
 */
const expandPlainDecimalText = (value) => {
  const raw = String(value ?? "").trim();

  if (!raw || !NUMERIC_TEXT_RE.test(raw)) {
    return null;
  }

  let normalized = raw.replace(",", ".").toLowerCase();

  let sign = "";

  if (normalized.startsWith("-")) {
    sign = "-";
    normalized = normalized.slice(1);
  } else if (normalized.startsWith("+")) {
    normalized = normalized.slice(1);
  }

  const [mantissaRaw, exponentRaw] =
    normalized.split("e");

  const exponent =
    exponentRaw === undefined
      ? 0
      : Number(exponentRaw);

  if (!Number.isInteger(exponent)) {
    return null;
  }

  let [integerPart = "0", fractionPart = ""] =
    mantissaRaw.split(".");

  if (!integerPart) {
    integerPart = "0";
  }

  const digits =
    `${integerPart}${fractionPart}` || "0";

  const decimalIndex =
    integerPart.length + exponent;

  let plain;

  if (decimalIndex <= 0) {
    plain =
      `0.${"0".repeat(Math.abs(decimalIndex))}${digits}`;
  } else if (decimalIndex >= digits.length) {
    plain =
      `${digits}${"0".repeat(decimalIndex - digits.length)}`;
  } else {
    plain =
      `${digits.slice(0, decimalIndex)}.${digits.slice(decimalIndex)}`;
  }

  let [whole = "0", fraction = ""] =
    plain.split(".");

  // Führende Nullen reduzieren, ohne den Wert zu verändern.
  whole =
    whole.replace(/^0+(?=\d)/, "") || "0";

  // Überflüssige Nachkommastellen entfernen.
  fraction =
    fraction.replace(/0+$/, "");

  plain =
    fraction
      ? `${whole}.${fraction}`
      : whole;

  // Negatives Null-Ergebnis verhindern.
  if (/^0(?:\.0*)?$/.test(plain)) {
    sign = "";
  }

  return `${sign}${plain}`;
};

const formatNumericTextForCsv = (
  value,
  {
    decimalComma = false,
  } = {}
) => {
  const plain =
    expandPlainDecimalText(value);

  if (plain === null) {
    return null;
  }

  return decimalComma
    ? plain.replace(".", ",")
    : plain;
};

/**
 * Converts JS numbers to a plain decimal string for machine-readable CSV.
 *
 * ALWAYS full signed numeric values — never "<0.000001" / display dust text.
 * Dust formatting belongs only in PDF/UI (see exportFormatting.js).
 */
const formatPlainNumber = (num, { maxFractionDigits = 18, decimalComma = false } = {}) => {
  if (!Number.isFinite(num)) return "";

  // High fixed precision, trim trailing zeros, keep sign (incl. tiny losses).
  let s = num.toFixed(maxFractionDigits);
  s = s.replace(/(\.\d*?)0+$/g, "$1").replace(/\.$/g, "");
  if (s === "-0") s = "0";

  if (decimalComma) s = s.replace(".", ",");
  return s;
};

const parseDateLikeToMs = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return 0;
    return value < 1e12 ? value * 1000 : value;
  }

  const s = String(value).trim();
  if (!s) return 0;

  let m = s.match(
    /^(\d{2})\.(\d{2})\.(\d{4})(?:,?\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/
  );
  if (m) {
    const [, dd, mm, yyyy, hh = "0", mi = "0", ss = "0"] = m;
    return new Date(
      Number(yyyy),
      Number(mm) - 1,
      Number(dd),
      Number(hh),
      Number(mi),
      Number(ss)
    ).getTime();
  }

  m = s.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:,?\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?)?$/i
  );
  if (m) {
    let [, p1, p2, yyyy, hh = "0", mi = "0", ss = "0", ampm = ""] = m;

    let day;
    let month;

    if (ampm) {
      month = Number(p1);
      day = Number(p2);
    } else if (Number(p1) > 12) {
      day = Number(p1);
      month = Number(p2);
    } else if (Number(p2) > 12) {
      month = Number(p1);
      day = Number(p2);
    } else {
      month = Number(p1);
      day = Number(p2);
    }

    let hour = Number(hh);
    if (ampm) {
      const upper = ampm.toUpperCase();
      if (upper === "PM" && hour < 12) hour += 12;
      if (upper === "AM" && hour === 12) hour = 0;
    }

    return new Date(
      Number(yyyy),
      month - 1,
      day,
      hour,
      Number(mi),
      Number(ss)
    ).getTime();
  }

  const parsed = new Date(s).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const escapeCsvCell = (v, delimiter = ";") => {
  if (v === null || v === undefined) return "";
  let s = String(v);
  // Quote if it contains delimiter, quotes, or newlines
  if (s.includes('"')) s = s.replace(/"/g, '""');
  if (s.includes(delimiter) || s.includes("\n") || s.includes("\r") || s.includes('"')) {
    return `"${s}"`;
  }
  return s;
};

export default function ExportButton({
  data,
  filename,
  columns,
  sortBy = "timestamp",
  disabled = false,
  headerMap = null,
  delimiter = ";",
  className = "",
  label = null,
  subtitle = null,
  badge = "CSV",
  decimalComma = null,

  // Spalten, deren numerische Strings ebenfalls lokalisiert werden sollen.
  numericColumns = [],

  title = null,
  disabledReason = null,
}) {
  const { language } = useLanguage();

  const handleExport = () => {
    if (disabled) return;
    if (!data || data.length === 0) {
      alert(t("noDataToExport", language));
      return;
    }

    const sortedData = [...data].sort((a, b) => {
      let aVal;
      let bVal;

      if (sortBy === "timestamp") {
        aVal = a.timestamp ?? a.block_timestamp ?? a.date ?? 0;
        bVal = b.timestamp ?? b.block_timestamp ?? b.date ?? 0;
      } else if (sortBy === "date") {
        aVal = a.date ?? a.timestamp ?? a.block_timestamp ?? 0;
        bVal = b.date ?? b.timestamp ?? b.block_timestamp ?? 0;
      } else {
        aVal = a[sortBy] ?? 0;
        bVal = b[sortBy] ?? 0;
      }

      aVal = parseDateLikeToMs(aVal);
      bVal = parseDateLikeToMs(bVal);

      return bVal - aVal;
    });

    const keys = columns || Object.keys(sortedData[0]);
    const headerLabels = keys.map((k) => (headerMap && headerMap[k] ? headerMap[k] : k));
    const useDecimalComma =
      typeof decimalComma === "boolean"
        ? decimalComma
        : language !== "en";

    const numericColumnSet =
      new Set(
        Array.isArray(numericColumns)
          ? numericColumns
          : []
      );

    const csvRows = [
      headerLabels.join(delimiter),
      ...sortedData.map((row) =>
        keys
          .map((key) => {
            let val = row[key];
            if (val === null || val === undefined) return "";

            /*
             * Echte Zahlen:
             * normale JS-Zahlen zentral formatieren.
             */
            if (isFiniteNumber(val)) {
              val = formatPlainNumber(val, {
                maxFractionDigits: 18,
                decimalComma: useDecimalComma,
              });
            }

            /*
             * Numerische Strings:
             * nur in ausdrücklich freigegebenen Zahlenspalten formatieren.
             *
             * Dadurch bleiben:
             * - Tx Hashes
             * - Event Keys
             * - Token Contracts
             * - Token IDs
             * - Datumsangaben
             *
             * garantiert unverändert.
             */
            else if (numericColumnSet.has(key)) {
              const formatted =
                formatNumericTextForCsv(val, {
                  decimalComma: useDecimalComma,
                });

              if (formatted !== null) {
                val = formatted;
              }
            }

            // Escape for CSV at the very end
            return escapeCsvCell(val, delimiter);
          })
          .join(delimiter)
      ),
    ];

    const csv = "\ufeff" + csvRows.join("\n"); // UTF-8 BOM for Excel
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, `${filename}.csv`);
  };

  const displayLabel = label || `${filename}.csv`;
  const line2 = subtitle || (language === "en" ? "Comma-separated values" : "CSV-Datei");
  const Icon = FileText;

  return (
    <div className={className}>
      <button
        type="button"
        onClick={handleExport}
        disabled={disabled}
        title={title || undefined}
        className={`group w-full inline-flex items-center justify-between gap-3 px-3.5 py-2.5 rounded-xl border shadow-sm transition-all backdrop-blur
          ${
            disabled
              ? "border-slate-200 bg-slate-50/60 text-slate-400 cursor-not-allowed"
              : "border-slate-200 bg-white/70 text-slate-800 hover:bg-white hover:border-slate-300 hover:shadow-md"
          }
        `}
      >
        <span className="min-w-0 inline-flex items-center gap-3">
          <span
            className={`grid place-items-center size-9 rounded-lg border transition-colors
              ${disabled ? "border-slate-200 bg-white" : "border-slate-200 bg-slate-50 group-hover:bg-slate-100"}
            `}
          >
            <Icon className="w-4 h-4" />
          </span>

          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold tracking-tight">{displayLabel}</span>
            <span className="block truncate text-[11px] text-slate-500">{line2}</span>
          </span>
        </span>

        <span className="inline-flex items-center gap-2">
          {disabled ? (
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-400">
              <Lock className="w-3.5 h-3.5" />
              {language === "en" ? "Locked" : "Gesperrt"}
            </span>
          ) : (
            <span className="inline-flex items-center gap-2">
              <span className="hidden sm:inline-flex px-2 py-1 text-[10px] font-semibold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                {badge}
              </span>
              <span className="grid place-items-center size-9 rounded-lg bg-slate-800 text-white shadow-sm group-hover:bg-slate-700 transition-colors">
                <Download className="w-4 h-4" />
              </span>
            </span>
          )}
        </span>
      </button>

      {disabled && (
        <div className="text-[11px] text-slate-500 mt-1 text-center">
          {disabledReason ||
            (language === "en"
              ? "Available after purchase"
              : "Nach Kauf verfügbar")}
        </div>
      )}
    </div>
  );
}